/* eslint-disable no-redeclare */
import { clamp } from '@lemonade-hq/ts-helpers';
import { useCallback, useEffect, useRef, useState } from 'react';

export interface UseCountingIntervalProps {
  interval: number;
  to: number;
  from: number;
}

interface UseCountingIntervalReturnType extends UseCountingIntervalProps {
  delay: boolean;
}

interface UseCountingIntervalReturnType2 extends UseCountingIntervalProps {
  delay: boolean | number;
}

export function useCountingInterval({
  interval,
  delay,
  to,
  from,
}: UseCountingIntervalReturnType): [number, boolean, (fromOverride?: number) => void, () => void];

export function useCountingInterval({ interval, delay, to, from }: UseCountingIntervalReturnType): number;
export function useCountingInterval({
  interval,
  delay,
  to,
  from,
}: UseCountingIntervalReturnType2): number | [number, boolean, () => void, () => void] {
  const [counter, setCounter] = useState(from);
  const [isCounting, setIsCounting] = useState(false);
  const intervalRef = useRef<number>();
  const direction = to > from ? 1 : -1;

  const play = useCallback(
    (fromOverride?: number) => {
      setCounter(fromOverride ?? from);
      setIsCounting(true);
      intervalRef.current = setInterval(() => {
        setCounter(val => val + direction);
      }, interval) as unknown as number;

      return () => clearInterval(intervalRef.current);
    },
    [direction, interval, from],
  );

  const stop = useCallback(() => {
    setIsCounting(false);
    clearInterval(intervalRef.current);
  }, []);

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    setCounter(from);

    if (!Number.isInteger(delay)) return;

    // eslint-disable-next-line @typescript-eslint/no-empty-function
    let clear = (): void => {};
    const timeout = setTimeout(() => {
      clear = play();
    }, delay as number);

    return () => {
      clearTimeout(timeout);
      clear();
    };
  }, [delay, direction, interval, to, from, play]);

  useEffect(() => {
    if (counter === to) {
      setIsCounting(false);
      clearInterval(intervalRef.current);
    }
  }, [counter, to]);

  return Number.isInteger(delay) ? clamp(from, to, counter) : [clamp(from, to, counter), isCounting, play, stop];
}
